import React, { useState } from 'react';
import { Calculator as CalcIcon, RefreshCw, Box, Scale, Ruler } from 'lucide-react';
import RevealOnScroll from './RevealOnScroll';

const Calculator: React.FC = () => {
  const [weight, setWeight] = useState<string>('');
  const [length, setLength] = useState<string>('');
  const [width, setWidth] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  
  const [result, setResult] = useState<{
    chargeableWeight: number;
    cost: number;
    isVolumetric: boolean;
    tierName: string;
    breakdown?: string;
  } | null>(null);

  const calculateCost = () => {
    const w = parseFloat(weight) || 0;
    const l = parseFloat(length) || 0;
    const wid = parseFloat(width) || 0;
    const h = parseFloat(height) || 0;

    if (w <= 0 && (l <= 0 || wid <= 0 || h <= 0)) return;

    // Calculate Volumetric Weight
    const volWeight = (l * wid * h) / 5000;
    
    // Determine Chargeable Weight (Higher of Actual vs Volumetric)
    // If user hasn't entered dimensions, use actual weight
    const hasDimensions = l > 0 && wid > 0 && h > 0;
    const chargeableWeight = hasDimensions ? Math.max(w, volWeight) : w;

    let cost = 0;
    let tierName = '';
    let breakdown = '';

    // New Pricing Logic
    if (chargeableWeight <= 1) {
      // Tier 1: 1kg or below
      cost = 7.50;
      tierName = 'Minimum Rate';
      breakdown = 'Flat rate for 1st kg';
    } else if (chargeableWeight > 1 && chargeableWeight <= 5) {
      // Tier 2: 1kg - 5kg
      // Formula: 7.5 (1st kg) + (Weight - 1) * 4.5
      const additionalWeight = chargeableWeight - 1;
      cost = 7.50 + (additionalWeight * 4.50);
      tierName = 'Standard Rate';
      breakdown = `$7.50 (1st kg) + $${(additionalWeight * 4.50).toFixed(2)} (${additionalWeight.toFixed(2)} kg x $4.50)`;
    } else {
      // Tier 3: Above 5kg
      // Formula: Total Weight * 4.5
      cost = chargeableWeight * 4.50;
      tierName = 'Bulk Rate';
      breakdown = `${chargeableWeight.toFixed(2)} kg x $4.50 flat rate`;
    }

    setResult({
      chargeableWeight,
      cost,
      isVolumetric: hasDimensions && volWeight > w,
      tierName,
      breakdown
    });
  };

  const handleReset = () => {
    setWeight('');
    setLength('');
    setWidth('');
    setHeight('');
    setResult(null);
  };

  const TIERS = [
    { name: 'Minimum', range: '0 - 1 kg', price: '$7.50', unit: 'Flat Rate', desc: 'First kg' },
    { name: 'Standard', range: '1 - 5 kg', price: '$4.50', unit: 'per add. kg', desc: '$7.50 for 1st kg + $4.50/extra' },
    { name: 'Bulk', range: '5+ kg', price: '$4.50', unit: 'per kg', desc: 'Flat rate on total weight' },
  ];

  return (
    <section id="calculator" className="py-12 md:py-24 bg-slate-50 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center mb-8 md:mb-12">
           <h2 className="text-2xl md:text-4xl font-extrabold text-slate-900 mb-3 md:mb-4">
             We Offer The <span className="text-brand-600">Lowest International Shipping Rates</span> Around
           </h2>
           <p className="text-sm md:text-base text-slate-500 max-w-2xl mx-auto">
             Transparent pricing with no hidden fees. Pay based on the weight of your package.
           </p>
        </div>

        <RevealOnScroll>
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-start">
            
            {/* Rate Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 order-2 lg:order-1">
              {TIERS.map((tier, idx) => (
                <div key={idx} className="bg-white p-5 md:p-6 rounded-xl shadow-sm border border-slate-200 hover:border-brand-300 transition-colors flex flex-col justify-between">
                  <div>
                    <h3 className="text-base md:text-lg font-bold text-slate-800 mb-1">{tier.name}</h3>
                    <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-3 md:mb-4">{tier.range}</p>
                    <div className="flex flex-col">
                      <span className="text-2xl md:text-3xl font-extrabold text-brand-600">{tier.price}</span>
                      <span className="text-xs md:text-sm text-slate-500 font-medium">{tier.unit}</span>
                    </div>
                  </div>
                  <p className="text-xs md:text-sm text-slate-400 mt-3 pt-3 border-t border-slate-100">{tier.desc}</p>
                </div>
              ))}
              
              <div className="bg-blue-50 p-5 md:p-6 rounded-xl border border-blue-100 sm:col-span-3">
                <div className="flex items-start gap-3">
                  <Box className="w-6 h-6 text-brand-600 mt-1 shrink-0" />
                  <div>
                    <h4 className="font-bold text-brand-900 text-sm md:text-base">Dimensional Weight Policy</h4>
                    <p className="text-xs md:text-sm text-brand-800 mt-1 leading-relaxed">
                      Shipping cost is based on the greater of actual weight or volumetric weight. 
                      <br/>
                      <span className="font-mono text-xs bg-brand-100 px-2 py-1 rounded mt-2 inline-block">
                        (Length x Width x Height cm) ÷ 5000
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Calculator */}
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden order-1 lg:order-2">
              <div className="bg-brand-900 p-5 md:p-6 text-white">
                <h3 className="text-lg md:text-xl font-bold flex items-center gap-2">
                  <CalcIcon className="w-5 h-5 md:w-6 md:h-6" /> Shipping Calculator
                </h3>
                <p className="text-brand-200 text-xs md:text-sm mt-1">Estimate your shipping costs instantly</p>
              </div>

              <div className="p-5 md:p-8">
                {/* Weight Input */}
                <div className="mb-5 md:mb-6">
                  <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                    <Scale className="w-4 h-4 text-slate-400" /> Actual Weight (kg)
                  </label>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    placeholder="e.g. 2.5"
                    step="0.1"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition text-base"
                  />
                </div>

                {/* Dimensions Inputs */}
                <div className="mb-6 md:mb-8">
                  <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                    <Ruler className="w-4 h-4 text-slate-400" /> Dimensions (cm) <span className="text-slate-400 font-normal text-xs">(Optional)</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <input
                      type="number"
                      value={length}
                      onChange={(e) => setLength(e.target.value)}
                      placeholder="L"
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition text-base"
                    />
                    <input
                      type="number"
                      value={width}
                      onChange={(e) => setWidth(e.target.value)}
                      placeholder="W"
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition text-base"
                    />
                    <input
                      type="number"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      placeholder="H"
                      className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 outline-none transition text-base"
                    />
                  </div>
                </div>

                <button
                  onClick={calculateCost}
                  className="w-full bg-brand-600 hover:bg-brand-700 text-white font-bold py-3.5 md:py-4 rounded-xl shadow-lg transition-transform active:scale-95 mb-6 text-base md:text-lg"
                >
                  Calculate My Rate
                </button>

                {/* Results Display */}
                {result && (
                  <div className="animate-fade-in bg-slate-50 rounded-xl p-4 md:p-5 border border-slate-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-slate-500 font-medium">Applied Rate</span>
                      <span className="text-xs font-bold text-brand-600 bg-brand-50 px-2 py-1 rounded-full border border-brand-100">
                        {result.tierName}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-sm text-slate-500 font-medium">Chargeable Weight</span>
                      <span className="text-sm font-bold text-slate-800">
                        {result.chargeableWeight.toFixed(2)} kg
                        {result.isVolumetric && <span className="text-xs text-orange-500 ml-1">(Volumetric)</span>}
                      </span>
                    </div>

                    <div className="mb-4 text-xs text-slate-400 border-l-2 border-slate-300 pl-2">
                       {result.breakdown}
                    </div>

                    <div className="border-t border-slate-200 pt-4 flex justify-between items-center">
                      <span className="font-bold text-slate-900">Total Estimate</span>
                      <span className="text-2xl md:text-3xl font-extrabold text-brand-600">${result.cost.toFixed(2)}</span>
                    </div>

                    <div className="mt-4 text-center">
                      <button onClick={handleReset} className="text-xs text-slate-400 hover:text-brand-600 flex items-center justify-center gap-1 mx-auto transition-colors">
                        <RefreshCw size={12} /> Reset Calculator
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </RevealOnScroll>
        
        <p className="text-center text-xs text-slate-400 mt-8 md:mt-12 max-w-3xl mx-auto px-4">
           *Rates are subject to change. Final shipping costs may vary based on actual package characteristics and additional services requested.
        </p>

      </div>
    </section>
  );
};

export default Calculator;